import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useConsultationDrawer } from "@/context/ConsultationDrawerContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { X } from "lucide-react";

const ConsultationForm = lazy(() => import("./ConsultationForm"));

const GlobalConsultationDrawer = () => {
  const { isOpen, closeDrawer } = useConsultationDrawer();
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  
  const titles: Record<string, string> = {
    tr: 'Ücretsiz Konsültasyon',
    fr: 'Consultation Gratuite',
    en: 'Free Consultation',
  };
  const subtitles: Record<string, string> = {
    tr: 'Formu doldurun, size 24 saat içinde dönüş yapalım',
    fr: 'Remplissez le formulaire, nous vous répondrons sous 24 heures',
    en: "Fill the form and we'll get back to you within 24 hours",
  };
  const lang = i18n.language;
  const title = titles[lang] || titles.en;
  const subtitle = subtitles[lang] || subtitles.en;

  // Mobile: Bottom sheet drawer
  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={closeDrawer}>
        <SheetContent 
          side="bottom" 
          className="h-[90dvh] w-full overflow-y-auto p-0 rounded-t-3xl animate-in slide-in-from-bottom duration-300"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            setTimeout(() => {
              const firstInput = document.querySelector('.global-consultation-drawer input[name="name"]') as HTMLInputElement;
              if (firstInput) firstInput.focus();
            }, 100);
          }}
        >
          <SheetHeader className="sticky top-0 bg-background/95 backdrop-blur-sm z-10 px-4 py-4 border-b border-gold/20 relative">
            <button
              onClick={closeDrawer}
              className="absolute right-4 top-3 z-20 p-2 rounded-full bg-white border border-gray-300 shadow-md hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
            <div className="w-12 h-1 bg-muted-foreground/30 rounded-full mx-auto mb-2" />
            <SheetTitle className="text-center text-lg font-semibold text-foreground">
              {title}
            </SheetTitle>
          </SheetHeader>
          
          <div className="px-4 pt-4 pb-8 global-consultation-drawer">
            <Suspense fallback={
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
              </div>
            }>
              <ConsultationForm isDrawerMode onSuccess={closeDrawer} />
            </Suspense>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Centered dialog modal with animation
  return (
    <Dialog open={isOpen} onOpenChange={closeDrawer}>
      <DialogContent 
        className="max-w-xl w-full max-h-[85vh] overflow-y-auto bg-background/95 backdrop-blur-xl border border-gold/30 rounded-2xl shadow-2xl p-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-bottom-4 duration-300 [&>button:last-child]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => {
            const firstInput = document.querySelector('.global-consultation-drawer input[name="name"]') as HTMLInputElement;
            if (firstInput) firstInput.focus();
          }, 100);
        }}
      >
        <DialogHeader className="sticky top-0 bg-background/95 backdrop-blur-sm z-10 px-6 py-5 border-b border-gold/20 relative">
          <button
            onClick={closeDrawer}
            className="absolute right-4 top-4 z-30 p-2 rounded-full bg-white border border-gray-300 shadow-md hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
          <DialogTitle className="text-center text-xl font-semibold text-foreground px-8">
            {title}
          </DialogTitle>
          <p className="text-center text-sm text-muted-foreground mt-1">
            {subtitle}
          </p>
        </DialogHeader>
        
        <div className="px-6 pt-2 pb-6 global-consultation-drawer">
          <Suspense fallback={
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
            </div>
          }>
            <ConsultationForm isDrawerMode onSuccess={closeDrawer} />
          </Suspense>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalConsultationDrawer;
